import React, { memo, useContext } from 'react';
import useResizeObserver from 'use-resize-observer';

import Icon from 'flow/components/ui-components/Icon';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import useMediaQuery from 'flow/hooks/useMediaQuery';
import IconButton from 'flow/components/ui-components/IconButton';
import CallOnOtherDevice from 'flow/components/communication-canvas/CallOnOtherDevice';
import CallTitle from 'flow/components/communication-canvas/CallTitle';
import FocusedLocalCallContext from 'flow/contexts/call-item-size-context';
import AvatarDescription from 'flow/components/communication-canvas/AvatarDescription';
import CentionErrandWidget from 'flow/components/communication-canvas/CentionErrandWidget';
import CoListeningSubjectBanner from 'flow/components/communication-canvas/CoListeningSubjectBanner';
import RedirectPresentationWidget from 'flow/components/communication-canvas/RedirectPresentationWidget';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import {
    getCallItemContactTitle,
    getCallItemContactSubtitleExtended,
} from 'flow/utils/contact-utils';
import OmniTicketWidget from './OmniTicketWidget';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact } from 'flow/types/Contact';
import { ActiveLineData } from 'flow/types/ActiveLineData';

interface Props {
    onHangup?: () => void;
    onDecline?: () => void;
    onHold?: () => void;
    onUnhold?: () => void;
    onAnswer?: () => void;
    onSwap?: () => void;
    onTransferStart?: () => void;
    onTransferComplete?: () => void;
    onTransferCancel?: () => void;
    onMerge?: () => void;
    callId?: string;
    startTime?: Date;
    failCause?: string;
    contact?: TypedMap<Contact>;
    direction?: string;
    isAccepted?: boolean;
    remoteNumber?: string;
    ownCountryCode?: string;
    isEnded?: boolean;
    agentInfo?: string;
    activeCall?: TypedMap<ActiveLineData>;
    isAwaitingTransfer?: boolean;
    isConference?: boolean;
    conferenceNumber?: string;
    conferenceMeetingCode?: string;
    conferencePinCode?: string;
    getPinCode?: (meetingCode: string) => Promise<void>;
    centionDeeplink?: string;
    isOnHold?: boolean;
    conferenceToMeeting?: () => void;
    isRemote?: boolean;
}

const getWidgetContainer = (
    isFocused: boolean,
    activeCall: TypedMap<ActiveLineData>,
    centionDeeplink: string,
    callId: string,
) => (
    <div className="flex py-2 last:mb-0 empty:p-0">
        {isFocused && <RedirectPresentationWidget activeCall={activeCall} />}
        {isFocused && <CoListeningSubjectBanner callId={callId} />}
        {centionDeeplink && <CentionErrandWidget deeplink={centionDeeplink} />}
    </div>
);

const SmallCallItem = ({
    onHangup,
    onDecline,
    onHold,
    onUnhold,
    onAnswer,
    onSwap,
    onTransferStart,
    onTransferComplete,
    onTransferCancel,
    onMerge,
    callId,
    startTime,
    isOnHold,
    failCause,
    contact,
    direction,
    isAccepted,
    remoteNumber,
    ownCountryCode,
    isEnded,
    agentInfo,
    activeCall,
    isAwaitingTransfer,
    isConference,
    conferenceNumber,
    conferenceMeetingCode,
    conferencePinCode,
    getPinCode,
    conferenceToMeeting,
    centionDeeplink,
    isRemote,
}: Props) => {
    const { i18next } = useAppContext();
    const { ref } = useResizeObserver();

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);

    const displayMode = useAppSelector((state) =>
        state.app.get('callCanvasSize'),
    );

    const callCanvasMinimized = DisplayMode.MINIMIZED.is(displayMode);
    const isMinimized = useAppSelector((state) =>
        DisplayMode.MINIMIZED.is(
            state.meeting.getIn(['activeMeeting', 'meetingView']),
        ),
    );

    const omniTicketId = activeCall?.getIn([
        'bagVariables',
        'telavox.omni.ticket_id',
    ]);

    const title =
        getCallItemContactTitle(
            contact,
            agentInfo,
            remoteNumber,
            ownCountryCode,
            i18next,
        ) ?? '';
    const subtitle =
        getCallItemContactSubtitleExtended(
            title,
            contact,
            remoteNumber,
            ownCountryCode,
            i18next,
        ) ?? '';

    const titleInfo = isConference
        ? {
              title: i18next.t('conference.call'),
              subtitle: conferenceNumber,
          }
        : {
              title: title ? title.text : null,
              subtitle: subtitle,
          };

    const focusedLocalCallId = useContext(FocusedLocalCallContext);
    const isFocused = !focusedLocalCallId || focusedLocalCallId === callId;

    const answerButton = (
        <IconButton
            dataTestId="answer-button"
            onClick={onAnswer}
            tooltipLabel={i18next.t('Call.Canvas.Title.Answer')}
            className={twMerge(
                'hover:brighten-10 size-10 rounded-xl text-white',
                activeCall?.get('isOmnichannelErrand') ? 'bg-link' : 'bg-green',
            )}
        >
            <Icon
                iconKey={
                    activeCall?.get('isOmnichannelErrand') ? 'email' : 'call'
                }
            />
        </IconButton>
    );

    const displayBlock = useMediaQuery('all and (max-width: 600px)');

    return (
        <div
            className={twMerge(
                'group @container flex items-center rounded-sm bg-gray-800 p-2',
                isMinimized ? 'flex-1' : '',
                isSinglePaneMode
                    ? 'block flex-auto flex-col space-y-2'
                    : 'flex flex-1 gap-2',
            )}
            data-test-id="small-call-item"
        >
            {isRemote && (
                <CallOnOtherDevice
                    className={twMerge(
                        'h-full px-2 py-1 text-xs',
                        !isSinglePaneMode && 'w-auto',
                    )}
                />
            )}
            <div
                className={twMerge(
                    'flex min-w-0 flex-1 items-center',
                    callCanvasMinimized ? 'h-10 min-h-10' : 'h-min-20 gap-1',
                    isSinglePaneMode ? 'w-full' : '',
                )}
                ref={ref}
            >
                <AvatarDescription
                    contact={contact}
                    callCanvasMinimized={callCanvasMinimized}
                    direction={direction}
                    isAccepted={isAccepted}
                    isOnHold={isOnHold}
                    isConference={isConference}
                    conferenceMeetingCode={conferenceMeetingCode}
                    conferencePinCode={conferencePinCode}
                    getPinCode={getPinCode}
                    title={titleInfo.title}
                    subtitle={titleInfo.subtitle}
                />
                {getWidgetContainer(
                    isFocused,
                    activeCall,
                    centionDeeplink,
                    callId,
                )}
                <OmniTicketWidget
                    activeCallKey={activeCall?.get('key')}
                    ticketKey={
                        omniTicketId ? `ticket-${omniTicketId}` : undefined
                    }
                    className="min-w-0 px-2"
                />
                <div className="flex shrink-0 justify-end gap-3">
                    <div
                        className={twMerge(
                            'flex items-center',
                            !isAwaitingTransfer
                                ? 'group-hover:!hidden sm:group-hover:!flex'
                                : null,
                        )}
                    >
                        <CallTitle
                            direction={direction}
                            failCause={failCause}
                            startTime={startTime}
                            isOnHold={isOnHold}
                            isEnded={isEnded}
                            isAwaitingTransfer={isAwaitingTransfer}
                            useOmniErrandTerminologyForIncomingItem={activeCall?.get(
                                'isOmnichannelErrand',
                            )}
                        />
                    </div>
                    <div
                        className={twMerge(
                            'flex gap-3',
                            !isAwaitingTransfer
                                ? 'max-sm:sr-only-until-hover-or-focus !flex'
                                : null,
                        )}
                    >
                        {onHangup && (
                            <IconButton
                                dataTestId="hangup-button"
                                onClick={onHangup}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.HangUp',
                                )}
                                className="bg-red size-10 rounded-xl text-white"
                            >
                                <Icon iconKey="call_end" />
                            </IconButton>
                        )}
                        {onDecline && (
                            <IconButton
                                dataTestId="hangup-button"
                                onClick={onDecline}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.Dismiss',
                                )}
                                className="bg-red size-10 rounded-xl text-white"
                            >
                                <Icon iconKey="call_end" />
                            </IconButton>
                        )}
                        {onSwap && (
                            <IconButton
                                onClick={onSwap}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.Swap',
                                )}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="swapCalls" />
                            </IconButton>
                        )}
                        {!onSwap && onHold && (
                            <IconButton
                                dataTestId="hold-button"
                                onClick={onHold}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.Park',
                                )}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="pause" />
                            </IconButton>
                        )}
                        {!onSwap && onUnhold && (
                            <IconButton
                                onClick={onUnhold}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.Unpark',
                                )}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="play_arrow" />
                            </IconButton>
                        )}
                        {onAnswer && answerButton}
                        {onTransferStart && (
                            <IconButton
                                onClick={onTransferStart}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.TransferStart',
                                )}
                            >
                                <Icon iconKey="transfer_call" />
                            </IconButton>
                        )}
                        {onTransferCancel && (
                            <IconButton
                                onClick={onTransferCancel}
                                tooltipLabel={i18next.t('cancel')}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="call_end" />
                            </IconButton>
                        )}
                        {onTransferComplete && (
                            <IconButton
                                onClick={onTransferComplete}
                                className="bg-green hover:darken-10 size-10 rounded-xl text-white"
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.TransferComplete',
                                )}
                            >
                                <Icon iconKey="transfer_call" />
                            </IconButton>
                        )}
                        {onMerge && (
                            <IconButton
                                onClick={onMerge}
                                tooltipLabel={i18next.t('call.merge')}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="merge_call" />
                            </IconButton>
                        )}
                        {conferenceToMeeting && (
                            <IconButton
                                onClick={conferenceToMeeting}
                                tooltipLabel={i18next.t(
                                    'Call.Canvas.Title.Rejoin_With_Video',
                                )}
                                className="hover:brighten-10 size-10 rounded-xl bg-gray-600 text-white"
                            >
                                <Icon iconKey="video" />
                            </IconButton>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(SmallCallItem);
